/**
 * Common utilities for DHTMLX Gantt link formatting and rendering.
 * Provides generic functions for correlative ID retrieval, duration formatter creation,
 * link formatter creation, and generic link rendering with caching.
 */

/**
 * Retrieve the correlative ID for a task.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {number|string} taskId - Internal task ID.
 * @returns {number|string} The correlative_id or original taskId if unavailable.
 */
export function getCorrelativeId(gantt, taskId) {
  const task = gantt.getTask(taskId);
  return task?.correlative_id ?? taskId;
}

/**
 * Create a DHTMLX PRO DurationFormatter instance configured for day-based parsing and auto formatting.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @returns {Object} DurationFormatter instance.
 */
export function createDurationFormatter(gantt) {
  return gantt.ext.formatters.durationFormatter({
    enter: 'day',
    store: 'hour',
    format: 'auto'
  });
}

/**
 * Create a custom LinkFormatter wrapping the DHTMLX PRO linkFormatter.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} durationFormatter - The DurationFormatter to use for lag formatting.
 * @param {string} propertyKey - Link property to use for correlative ID ('source' or 'target').
 * @param {Function} mapTypeFn - Function to map link.type code to two-letter code (e.g. '0' -> 'fs').
 * @returns {Object} An object with `format(link): string` and `parse(text): Object` methods.
 */
export function createCustomLinkFormatterCommon(
  gantt,
  durationFormatter,
  propertyKey,
  mapTypeFn
) {
  const nativeParser = gantt.ext.formatters.linkFormatter({
    durationFormatter
  });
  return {
    format(link) {
      const corId = getCorrelativeId(gantt, link[propertyKey]);
      const typeCode = mapTypeFn(link.type);
      const lagVal = link.lag ?? 0;
      // Determine sign
      const operator = lagVal >= 0 ? '+' : '-';
      // Always display lag in days to avoid month/year formatting
      const hoursPerDay = gantt.config.hoursPerDay || 24;
      const absLag = Math.abs(lagVal);
      let lagNum;
      if (absLag === 0) {
        lagNum = '0';
      } else {
        const days = Math.round(absLag / hoursPerDay);
        lagNum = String(days);
      }
      return `${corId}${typeCode}${operator}${lagNum}d`;
    },
    parse(text) {
      const normalized = text
        // match digits followed by fs, ss, ff, or sf (case-insensitive)
        .replace(/(\d+)(fs|ss|ff|sf)/i, (m, id, type) => {
          const match = gantt.getTaskBy((t) => t.correlative_id == id)[0];
          return `${match?.id ?? id}${type}`;
        })
        // replace trailing 'd' with ' days'
        .replace(/d$/, ' days');
      return nativeParser.parse(normalized);
    }
  };
}

/**
 * Render links for a Gantt task using provided formatter and cache.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} formatter - LinkFormatter with a `format(link)` method.
 * @param {Object} activity - Gantt task object.
 * @param {string} propertyKey - Activity property containing link IDs (e.g. '$target' or '$source').
 * @param {WeakMap<Object, {signature: string, value: string}>} cacheMap - WeakMap for caching rendered strings per activity.
 * @returns {string} Semicolon-separated link strings.
 */
export function renderLinks(gantt, formatter, activity, propertyKey, cacheMap) {
  const links = activity[propertyKey] || [];
  const signature = links.join(',');
  const cacheEntry = cacheMap.get(activity);
  if (cacheEntry && cacheEntry.signature === signature) {
    return cacheEntry.value;
  }
  const getLinkFn = gantt.getLink.bind(gantt);
  const formatFn = formatter.format.bind(formatter);
  let rendered = '';
  for (let i = 0; i < links.length; i++) {
    try {
      const text = formatFn(getLinkFn(links[i]));
      if (text) {
        rendered += (rendered ? ';' : '') + text;
      }
    } catch (e) {
      // ignore individual link errors
    }
  }
  cacheMap.set(activity, { signature, value: rendered });
  return rendered;
}
